import { api } from '../api/client'

/** From WordPress (spec 2026-10-21-wp-pending-import): WordPress posts AAMCirc doesn't hold yet. */
export type WpPendingStatus = 'pending' | 'queued' | 'importing' | 'imported' | 'dismissed' | 'failed'
export type WpPendingEntry = {
  id: number
  site_type: 'main' | 'membership'
  wp_post_id: number
  title: string
  wp_url: string | null
  post_date: string | null
  wp_post_modified: string | null
  kind: 'new' | 'edited'
  status: WpPendingStatus
  desk_edited: boolean
  note: string | null
  error: string | null
  article_url: string | null
  imported_at: string | null
  requested_by: string | null
  /** Imported, landed unpublished and never put live: Approve & publish puts it up. */
  to_publish: boolean
  /** When it is scheduled to go live by itself (set in the Archive), if it is. */
  publish_at: string | null
  /** When the title's automatic switch put it live. */
  auto_published_at: string | null
}
export type WpPendingList = { items: WpPendingEntry[]; counts: Partial<Record<WpPendingStatus, number>>; scan_on: boolean; auto_on: boolean; since: string }
export type WpPendingView = 'open' | 'imported' | 'dismissed' | 'all'
export type WpPendingFilters = { status: WpPendingView; kind: '' | 'new' | 'edited'; site: '' | 'main' | 'membership' }
export type QueueResult = { queued: number; needs_confirm: number; skipped: number }

const BASE = '/api/editorial/wp-pending'

function query(pub: string, f: WpPendingFilters): string {
  const p = new URLSearchParams({ pub, status: f.status })
  if (f.kind) p.set('kind', f.kind)
  if (f.site) p.set('site', f.site)
  return p.toString()
}

export const listWpPending = (pub: string, f: WpPendingFilters) => api.get<WpPendingList>(`${BASE}?${query(pub, f)}`)
export const checkWpNow = (pub: string) => api.post<Record<string, number>>(`${BASE}/check`, { pub })
export const importWpPending = (pub: string, pick: { ids?: number[]; all?: boolean }, f: WpPendingFilters, confirm = false) =>
  api.post<QueueResult>(`${BASE}/import`, { pub, ...pick, kind: f.kind || null, site: f.site || null, confirm })
export const dismissWpPending = (pub: string, ids: number[]) => api.post<{ dismissed: number }>(`${BASE}/dismiss`, { pub, ids })
export type PublishResult = { published: number; refused: { id: number; title: string; reason: string }[] }
export const publishWpPending = (pub: string, pick: { ids?: number[]; all?: boolean }, f: WpPendingFilters) =>
  api.post<PublishResult>(`${BASE}/publish`, { pub, ...pick, kind: f.kind || null, site: f.site || null })
export const restoreWpPending = (pub: string, ids: number[]) => api.post<{ restored: number }>(`${BASE}/restore`, { pub, ids })

const STATUS: Record<WpPendingStatus, string> = {
  pending: 'Waiting', queued: 'Queued', importing: 'Importing…', imported: 'On the site', dismissed: 'Left off', failed: 'Not imported',
}
export const statusText = (s: WpPendingStatus): string => STATUS[s] ?? s

/** The status column: an imported post that hasn't gone live yet says so, not "On the site". */
export function rowStatusText(e: Pick<WpPendingEntry, 'status' | 'to_publish' | 'publish_at'> & { auto_published_at?: string | null }): string {
  if (e.status === 'imported' && e.to_publish) {
    if (e.publish_at) {
      const at = new Date(e.publish_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
      return `Imported, goes live ${at}`
    }
    return 'Imported, not live yet'
  }
  if (e.status === 'imported' && e.auto_published_at) return 'On the site (published automatically)'
  return statusText(e.status)
}

/** Imported posts waiting for an editor's Approve & publish. */
export const publishable = (e: Pick<WpPendingEntry, 'status' | 'to_publish'>): boolean => e.status === 'imported' && e.to_publish

/** Free site or members' site, and new or changed since it came over, in a few words. */
export function whatText(e: Pick<WpPendingEntry, 'site_type' | 'kind'>): string {
  const where = e.site_type === 'membership' ? "Members'" : 'Free'
  return e.kind === 'new' ? `${where} · new` : `${where} · changed in WordPress`
}

/** Rows that can be imported or left off (the others are in hand or done). */
export const actionable = (e: Pick<WpPendingEntry, 'status'>): boolean => e.status === 'pending' || e.status === 'failed'

/** Poll while the worker has something to do. */
export const busy = (items: Pick<WpPendingEntry, 'status'>[] | undefined): boolean =>
  !!items?.some((e) => e.status === 'queued' || e.status === 'importing')

/** What happened after pressing Import, in words. */
export function queuedText(r: QueueResult): string {
  const parts: string[] = []
  if (r.queued) parts.push(`${r.queued} ${r.queued === 1 ? 'post is' : 'posts are'} on the way; each takes about a minute. A new post lands unpublished: press Approve & publish when it's in.`)
  if (r.needs_confirm) parts.push(`${r.needs_confirm} ${r.needs_confirm === 1 ? 'was' : 'were'} edited in the desk since coming over: use Replace the desk's version on ${r.needs_confirm === 1 ? 'it' : 'each'}.`)
  if (!parts.length) parts.push('Nothing to import.')
  return parts.join(' ')
}

/** What happened after pressing Approve & publish, in words. */
export function publishedText(r: PublishResult): string {
  const parts: string[] = []
  if (r.published) parts.push(`${r.published} ${r.published === 1 ? 'post is' : 'posts are'} live on the site.`)
  for (const x of r.refused) parts.push(`“${x.title}” wasn't published: ${x.reason}`)
  if (!parts.length) parts.push('Nothing to publish.')
  return parts.join(' ')
}
